import { useState } from 'react';
import { useAuth } from '@/context';

import { LoginForm } from '../Auth/LoginForm';
import { RegisterForm } from '../Auth/RegisterForm';
import { ApplePayButton } from './ApplePayButton';
import styles from './QuickBuyModal.module.css';

interface QuickBuyModalProps {
    isOpen: boolean;
    onClose: () => void;
    product: any;
    options: any; // The selected options (meta)
    totalPrice: number; // calculated total price
    onPaymentSuccess: () => void;
}

export const QuickBuyModal = ({ isOpen, onClose, product, options, totalPrice, onPaymentSuccess }: QuickBuyModalProps) => {
    const { isLoggedIn } = useAuth();
    const [authMode, setAuthMode] = useState<'login' | 'register'>('login');

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className={styles.overlay} onClick={handleBackdropClick}>
            <div className={styles.modal}>
                <div
                    className={styles.closeButton}
                    onClick={onClose}
                    role="button"
                    tabIndex={0}
                    aria-label="Fermer"
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>

                <div className={styles.content}>
                    {!isLoggedIn ? (
                        <div className={styles.authSection}>
                            <h2 className={styles.title}>
                                {authMode === 'login' ? 'Connexion' : 'Créer un compte'}
                            </h2>
                            <p className={styles.subtitle}>
                                Pour finaliser votre achat, veuillez vous identifier.
                            </p>

                            {authMode === 'login' ? (
                                <>
                                    <LoginForm onSuccess={() => { }} redirectAfterLogin={false} />
                                    <p className={styles.switchAuth}>
                                        Pas encore de compte ? <button onClick={() => setAuthMode('register')}>S'inscrire</button>
                                    </p>
                                </>
                            ) : (
                                <>
                                    <RegisterForm onSuccess={() => { }} redirectAfterRegister={false} />
                                    <p className={styles.switchAuth}>
                                        Déjà un compte ? <button onClick={() => setAuthMode('login')}>Se connecter</button>
                                    </p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className={styles.summarySection}>
                            <h2 className={styles.title}>Récapitulatif</h2>

                            <div className={styles.productSummary}>
                                <div className={styles.productImage}>
                                    {product.images && product.images[0] && (
                                        <img src={product.images[0].src} alt={product.name} />
                                    )}
                                </div>
                                <div className={styles.productDetails}>
                                    <h3>{product.name}</h3>
                                    {options && (
                                        <div className={styles.optionsList}>
                                            {options.map((opt: any, idx: number) => (
                                                <div key={idx} className={styles.optionRow}>
                                                    <span>{opt.label}:</span> <span>{opt.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className={styles.totalPrice}>
                                        Total: {totalPrice.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.paymentSection}>
                                <p className={styles.paymentInfo}>
                                    Réglez simplement avec Apple Pay. L'adresse de livraison et de facturation seront récupérées automatiquement.
                                </p>
                                <div className={styles.applePayWrapper}>
                                    <ApplePayButton
                                        price={totalPrice}
                                        label={`Commander ${product.name}`}
                                        onPaymentMethod={async (ev) => {
                                            try {
                                                console.log("Payment method received", ev);

                                                // Extract Apple Pay Data
                                                // Construct Address
                                                const shippingContact = ev.shippingAddress;

                                                // Robust name parsing
                                                const rawName = (ev.payerName || '').trim();
                                                const spaceIndex = rawName.indexOf(' ');
                                                const firstName = spaceIndex > -1 ? rawName.substring(0, spaceIndex) : rawName;
                                                const lastName = spaceIndex > -1 ? rawName.substring(spaceIndex + 1) : rawName; // Use first name as last name if single word to pass validation

                                                const address = {
                                                    first_name: firstName || 'Client',
                                                    last_name: lastName || 'Inconnu',
                                                    address_1: shippingContact?.addressLines?.[0] || 'Adresse inconnue',
                                                    address_2: shippingContact?.addressLines?.[1] || '',
                                                    city: shippingContact?.locality || 'Ville imconnue',
                                                    postcode: shippingContact?.postalCode || '00000',
                                                    country: shippingContact?.countryCode || 'FR',
                                                    phone: ev.payerPhone || '',
                                                    email: ev.payerEmail || 'no-email@provided.com', // Should be captured if requested
                                                };

                                                // Create Order Payload
                                                // We need to match CheckoutData interface
                                                const { checkoutApi } = await import('@/api/client');

                                                // We assume items are in the cart OR we need to simulate adding them?
                                                // "Quick Buy" usually implies adding to cart first or creating a custom order.
                                                // Backend create_payment_intent uses CURRENT CART.
                                                // So we must ADD to cart first if not already there.

                                                const { cartApi } = await import('@/api/client');
                                                await cartApi.clearCart(); // Clear old items

                                                // Construct meta from options
                                                // We receive 'options' prop in QuickBuyModal which is array of {label, value}.
                                                // But we need the 'meta' object for API.
                                                // We don't have the raw meta object here easily unless passed.
                                                // SOLUTION: QuickBuyModal should receive raw 'meta' object if available.
                                                // NOTE: For now, I'll pass simple meta. 
                                                // We need to fix QuickBuyModal props to accept 'rawMeta' or similar.
                                                // I'll assume for now we just add the product ID and variation if needed.

                                                await cartApi.addItem({
                                                    product_id: product.id,
                                                    quantity: 1,
                                                    // meta: options.rawMeta
                                                });

                                                // Now create payment intent with the collected address
                                                const result = await checkoutApi.createPaymentIntent({
                                                    billing: address,
                                                    shipping: address,
                                                    ship_to_different_address: false,
                                                    create_account: false,
                                                    payment_method: 'stripe',
                                                });

                                                if (result.success && result.client_secret) {
                                                    // Confirm with Stripe
                                                    const { loadStripe } = await import('@stripe/stripe-js');
                                                    const stripe = await loadStripe((window as any).evcShop.stripeKey);

                                                    if (!stripe) {
                                                        throw new Error("Stripe failed to load");
                                                    }

                                                    // Confirm the PaymentIntent with the Apple Pay PaymentMethod
                                                    const confirmResult = await stripe.confirmCardPayment(result.client_secret, {
                                                        payment_method: ev.paymentMethod.id,
                                                    });

                                                    if (confirmResult.error) {
                                                        console.error("Confirmation error", confirmResult.error);
                                                        ev.complete('fail');
                                                    } else if (confirmResult.paymentIntent && confirmResult.paymentIntent.status === 'succeeded') {
                                                        // Manually complete order to ensure status is updated immediately
                                                        // This solves the "Pending Payment" display issue if webhook is slow
                                                        try {
                                                            const completeResult = await checkoutApi.completeOrder(
                                                                result.order_id,
                                                                confirmResult.paymentIntent.id
                                                            );
                                                            ev.complete('success');
                                                            if (onPaymentSuccess) onPaymentSuccess();

                                                            // Redirect with order number for confirmation page
                                                            // User requested to use /commander (checkout page) which handles confirmation via params
                                                            const checkoutUrl = (window as any).evcShop?.urls?.checkout || '/commander';
                                                            const separator = checkoutUrl.includes('?') ? '&' : '?';
                                                            window.location.href = `${checkoutUrl}${separator}key=${result.order_key}&order_number=${completeResult.order_number}`;
                                                        } catch (err) {
                                                            console.error("Failed to complete order backend", err);
                                                            // Fallback: assume webhook will handle it, but still redirect
                                                            ev.complete('success');
                                                            const checkoutUrl = (window as any).evcShop?.urls?.checkout || '/commander';
                                                            const separator = checkoutUrl.includes('?') ? '&' : '?';
                                                            window.location.href = `${checkoutUrl}${separator}key=${result.order_key}`;
                                                        }
                                                    } else {
                                                        ev.complete('fail');
                                                    }
                                                } else {
                                                    ev.complete('fail');
                                                }

                                            } catch (err) {
                                                console.error(err);
                                                ev.complete('fail');
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
